/**
 * Removes presentation-only HTML that is stored in configuration data before it is rendered.
 *
 * Why this exists:
 * - Some reward content is persisted with line-break markup.
 * - Cleaning this at render time improves accessibility without changing the stored source data.
 *
 * What it does:
 * - Strips `<br>` tags.
 * - Removes empty `<p>` elements, including variants that only contain whitespace, `&nbsp;`,
 *   or other presentation-only remnants.
 * - Uses DOM parsing when available so the browser can remove empty paragraphs reliably.
 */
export const removeBreaksAndEmptyParagraphs = (html: string): string => {
  const withoutBreaks = html.replace(/<br\s*\/?>/giu, '');

  if (typeof DOMParser === 'undefined') {
    // Fallback for environments where DOMParser is not available (for example, some tests).
    return withoutBreaks.replace(
      /<p\b[^>]*>(?:\s|&nbsp;|<br\s*\/?>|<span\b[^>]*>\s*<\/span>)*<\/p>/giu,
      ''
    );
  }

  // Parse the HTML so we can remove empty paragraphs from the actual DOM tree.
  const documentParser = new DOMParser();
  const parsedDocument = documentParser.parseFromString(`<div>${withoutBreaks}</div>`, 'text/html');
  const rootElement = parsedDocument.body.firstElementChild;

  if (rootElement === null) {
    return withoutBreaks;
  }

  // Remove paragraphs that have no meaningful content after presentation-only markup is stripped.
  rootElement.querySelectorAll('p').forEach((paragraph) => {
    const normalizedContent = paragraph.innerHTML
      .replace(/<br\s*\/?>/giu, '')
      .replace(/<span\b[^>]*>\s*<\/span>/giu, '')
      .replace(/&nbsp;/giu, '')
      .replace(/\s+/gu, '')
      .trim();

    if (normalizedContent === '') {
      paragraph.remove();
    }
  });

  return rootElement.innerHTML;
};
